import { z } from 'zod';
import { TenantIdSchema, IsoDateSchema } from './_primitives.js';

export const DOCK_APPOINTMENT_STATUS = Object.freeze({
  DRAFT:             'draft',
  LPNS_PENDING:      'lpns_pending',
  COUNTER_PROPOSED:  'counter_proposed',
  CONFIRMED:         'confirmed',
  REJECTED:          'rejected',
  CHECKED_IN:        'checked_in',
  IN_PROGRESS:       'in_progress',
  COMPLETED:         'completed',
  NO_SHOW:           'no_show',
  CANCELLED:         'cancelled',
});

export const DOCK_APPOINTMENT_DIRECTION = Object.freeze({
  INBOUND:  'inbound',
  OUTBOUND: 'outbound',
});

export const DOCK_DURATION_SOURCE = Object.freeze({
  MANUAL:      'manual',
  TEMPLATE:    'template',
  AEGIS_KPOL:  'aegis-kpol',
  RULE_ENGINE: 'rule-engine',
});

export const DockAppointmentStatusEnum = z.enum(Object.values(DOCK_APPOINTMENT_STATUS));

const DockCarrierSchema = z.object({
  name:          z.string().min(1),
  scac:          z.string().nullable().optional(),
  contactName:   z.string().nullable().optional(),
  contactEmail:  z.string().nullable().optional(),
  contactPhone:  z.string().nullable().optional(),
  driverName:    z.string().nullable().optional(),
  driverPhone:   z.string().nullable().optional(),
  tractorNumber: z.string().nullable().optional(),
  trailerNumber: z.string().nullable().optional(),
});

export const DockAppointmentSchema = z.object({
  id:                         z.string().optional(),
  tenantId:                   TenantIdSchema,
  appointmentCode:            z.string().optional(),

  // Identity and references
  facilityId:                 z.string().min(1),
  dockDoorId:                 z.string().nullable().optional(),
  direction:                  z.enum(['inbound', 'outbound']),
  customerId:                 z.string().min(1),

  // Schedule
  scheduledStart:             IsoDateSchema,
  scheduledEnd:               IsoDateSchema,
  scheduledDurationMinutes:   z.number().positive(),
  durationComputationSource:  z.enum(['manual', 'template', 'aegis-kpol', 'rule-engine']).optional(),

  // Carrier
  carrier:                    DockCarrierSchema,

  // State machine
  status:                     DockAppointmentStatusEnum,

  // Cross-module linkage (nullable; populated by other services)
  poNumbers:                  z.array(z.string()).nullable().optional(),
  lpnReferences:              z.array(z.string()).nullable().optional(),
  bolNumber:                  z.string().nullable().optional(),
  shipmentId:                 z.string().nullable().optional(),
  cartonizationDecisionId:    z.string().nullable().optional(),
  yardTrailerId:              z.string().nullable().optional(),

  // Lifecycle timestamps
  requestedAt:                IsoDateSchema,
  confirmedAt:                IsoDateSchema.nullable().optional(),
  rejectedAt:                 IsoDateSchema.nullable().optional(),
  gateInAt:                   IsoDateSchema.nullable().optional(),
  gateOutAt:                  IsoDateSchema.nullable().optional(),
  completedAt:                IsoDateSchema.nullable().optional(),
  cancelledAt:                IsoDateSchema.nullable().optional(),

  // Computed metrics
  dwellMinutes:               z.number().nullable().optional(),
  onTimeStatus:               z.enum(['on-time', 'early', 'late', 'no-show']).nullable().optional(),

  // Operational
  notes:                      z.string().nullable().optional(),
  internalNotes:              z.string().nullable().optional(),

  // RESERVED — populated by future Pylon sessions
  negotiationTrailId:         z.string().nullable().optional(), // PYLON-10
  predictiveDwell:            z.record(z.unknown()).nullable().optional(), // PYLON-15
  evidencePackageId:          z.string().nullable().optional(), // PYLON-16
  beaconBindings:             z.array(z.string()).nullable().optional(), // PYLON-14

  // Audit envelope
  createdAt:                  z.union([IsoDateSchema, z.object({})]).optional(),
  updatedAt:                  z.union([IsoDateSchema, z.object({})]).optional(),
  createdBy:                  z.string().optional(),
  updatedBy:                  z.string().optional(),
  softDeleted:                z.boolean().optional(),
}).passthrough();

/** @typedef {import('zod').infer<typeof DockAppointmentSchema>} DockAppointment */

export const DockAppointmentUpdateSchema = DockAppointmentSchema.partial();

// ── State machine validator ───────────────────────────────────────────────────

const _TRANSITIONS = Object.freeze({
  draft:            ['lpns_pending', 'confirmed', 'cancelled'],
  lpns_pending:     ['draft', 'confirmed', 'cancelled'],
  counter_proposed: ['counter_proposed', 'confirmed', 'rejected', 'cancelled'],
  confirmed:        ['counter_proposed', 'checked_in', 'no_show', 'cancelled'],
  checked_in:       ['in_progress', 'cancelled'],
  in_progress:      ['completed', 'cancelled'],
  rejected:         [],
  completed:        [],
  no_show:          [],
  cancelled:        [],
});

/**
 * Returns true if the status transition from → to is permitted.
 * Used as a gate before any status write; throwing is the caller's responsibility.
 *
 * @param {string} from
 * @param {string} to
 * @returns {boolean}
 */
export function canTransitionDockAppointment(from, to) {
  const allowed = _TRANSITIONS[from];
  if (!allowed) return false;
  return allowed.includes(to);
}
